import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import TermStatus from '../../../constants/termStatus';
import TerminateLink from './TerminateLink';
import { withSnackbar } from '../../snackbar/Snackbar';
import { history } from '../../../utils/history';
import { Tooltip } from '@cimpress/react-components';
import { actions as termActions, selectors as termSelectors } from '../../../reducers/terms';
import { connect } from 'react-redux';
import { PRICING_ARCH_V2 } from '../../../constants/priceArchs';
import { fetchFulfillerPricingArch } from '../../../reducers/identities';

class TermActions extends PureComponent {
  static defaultProps = {
    termRevision: null,
    buttonStyle: null,
    showViewMoreLink: false,
    className: ''
  };

  static propTypes = {
    termRevision: PropTypes.object.isRequired,
    updateTermStatus: PropTypes.func.isRequired,
    resetUpdateTermStatusStatus: PropTypes.func.isRequired,
    isStatusUpdating: PropTypes.bool,
    isStatusUpdated: PropTypes.bool,
    hasStatusUpdateFailed: PropTypes.bool,

    buttonStyle: PropTypes.any,
    onStatusUpdated: PropTypes.func,
    className: PropTypes.string
  };

  state = {
    disableTerminate: false
  };

  updateTermStatus = toStatus => {
    this.props.updateTermStatus(toStatus).then(
      () =>
        this.props.onStatusUpdated &&
        this.props.onStatusUpdated(
          {
            termId: this.props.termRevision.termId,
            revisionId: this.props.termRevision.revisionId + 1
          },
          toStatus
        )
    );
  };

  onUpdateStatusToPending = () => {
    this.updateTermStatus(TermStatus.PENDING);
  };

  onUpdateStatusToApproved = () => {
    this.updateTermStatus(TermStatus.APPROVED);
  };

  onUpdateStatusToTerminated = () => {
    this.updateTermStatus(TermStatus.TERMINATED);
  };

  componentDidMount() {
    this.props.resetUpdateTermStatusStatus();
    if (
      this.props.termRevision &&
      this.props.termRevision.seller &&
      this.props.termRevision.seller.accountId &&
      this.props.pricingArchDictionary
    ) {
      const arch = this.props.pricingArchDictionary[this.props.termRevision.seller.accountId];
      if (arch === undefined) {
        this.props.fetchFulfillerPricingArch(this.props.termRevision.seller.accountId);
      }
      if (arch === PRICING_ARCH_V2) {
        this.setState({ disableTerminate: true });
      }
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.pricingArchDictionary !== this.props.pricingArchDictionary) {
      const arch = this.props.pricingArchDictionary[this.props.termRevision.seller.accountId];
      if (arch === PRICING_ARCH_V2) {
        this.setState({ disableTerminate: true });
      }
    }
  }

  componentWillUnmount() {
    this.props.resetUpdateTermStatusStatus();
  }

  render() {
    const { t, termRevision, buttonStyle, className, isStatusUpdating, isStatusUpdated, showViewMoreLink } = this.props;

    const { disableTerminate } = this.state;

    const buttonsToRender = [];

    const hasAcceptorPermission = termRevision.userHasAuthorizationFor.acceptor;
    const hasInitiatorPermission = termRevision.userHasAuthorizationFor.initiator;

    switch (termRevision.status) {
      case TermStatus.DRAFT:
        if (hasInitiatorPermission) {
          buttonsToRender.push(
            <button
              key="publish"
              onClick={this.onUpdateStatusToPending}
              disabled={isStatusUpdating || isStatusUpdated}
              className={`btn btn-primary ${buttonStyle}`}>
              {t('contractActions:publish', { otherParty: termRevision.acceptor.name })}
            </button>
          );
        }
        break;
      case TermStatus.PENDING:
        // TODO: bring back reject button until we come to a clear definition on what rejecting means,
        // explore drop down option for buttons

        if (hasAcceptorPermission) {
          buttonsToRender.push(
            <button
              key="approve"
              onClick={this.onUpdateStatusToApproved}
              title={disableTerminate ? t('createContract:getStarted.sellerInPricingArchV2MessageTooltip') : null}
              disabled={isStatusUpdating || isStatusUpdated || disableTerminate}
              className={`btn btn-primary ${buttonStyle}`}>
              {t('contractActions:approve')}
            </button>
          );
        }
        break;
      default:
        break;
    }

    const viewPricingButton = (
      <Tooltip contents={t('contractActions:viewPricingToolTip')} key="viewmore">
        <button
          className={`btn btn-default ${buttonStyle}`}
          onClick={() => history.push(`/${termRevision.termId}/${termRevision.revisionId}`)}>
          {t('contractActions:viewPricing')}
        </button>
      </Tooltip>
    );

    const terminateButton = termRevision.status !== TermStatus.TERMINATED && (
      <TerminateLink
        key={'terminate'}
        termId={termRevision.termId}
        latestRevisionId={termRevision.revisionId}
        onTerminateAction={this.onUpdateStatusToTerminated}
        disabled={isStatusUpdating || isStatusUpdated || disableTerminate}
        terminated={isStatusUpdated}
      />
    );

    buttonsToRender.push(terminateButton);

    return (
      <div className={className}>
        {showViewMoreLink && <div style={{ float: 'left' }}>{viewPricingButton}</div>}
        <div style={{ float: 'right' }}>{buttonsToRender}</div>
      </div>
    );
  }
}

const mapStateToProps = state => ({
  isStatusUpdating: termSelectors.getIsTermCreating(state),
  pricingArchDictionary: state.identities.fulfillers.pricingArchDictionary
});

const mapDispatchToProps = (dispatch, ownProps) => {
  const { termRevision } = ownProps;
  return {
    updateTermStatus: toStatus =>
      dispatch(termActions.updateTermStatus(termRevision.termId, termRevision.revisionId, toStatus)),
    resetUpdateTermStatusStatus: () => dispatch(termActions.resetUpdateTermStatusStatus()),
    fetchFulfillerPricingArch: () => dispatch(fetchFulfillerPricingArch(termRevision.seller.accountId))
  };
};

export default withSnackbar(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(withTranslation()(TermActions))
);
